import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import DOMPurify from 'dompurify';
import { Clock, ArrowRight, ArrowLeft, ChevronRight } from 'lucide-react';
import { api } from '../../app/api';
import { logger } from '../../lib/logger';
import { Skeleton } from '../../components/ui/skeleton';
import { Button } from '../../components/ui/button';
import { Separator } from '../../components/ui/separator';
import { BlogShell } from './BlogShell';
import { useDocumentHead } from './useDocumentHead';

const SITE_BASE = 'https://apexathleticmind.com';
const DEFAULT_HERO = '/blog/heroes/5-steps-performance-anxiety-og.jpg';
const AUTHOR_NAME = 'Coach Varsay Lewis, M.Ed.';

// Allowlist of HTML tags + attributes permitted in blog post bodies.
// Admin-pasted HTML is sanitized with DOMPurify before being rendered to
// defend against accidental or hostile <script>/onerror/javascript: payloads
// even though the admin route is auth-gated. Defense in depth.
const SANITIZE_CONFIG = {
  ALLOWED_TAGS: [
    'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
    'p', 'br', 'hr', 'strong', 'em', 'b', 'i', 'u', 's', 'mark', 'small',
    'a', 'span',
    'ul', 'ol', 'li',
    'blockquote', 'cite', 'q',
    'code', 'pre',
    'img', 'figure', 'figcaption',
    'table', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td',
  ],
  ALLOWED_ATTR: [
    'href', 'target', 'rel', 'src', 'alt', 'title',
    'width', 'height', 'class', 'id',
  ],
  ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto|tel|ftp):|[^a-z]|[a-z+.-]+(?:[^a-z+.\-:]|$))/i,
};

// Register a one-time hook so DOMPurify hardens external <a> tags during
// sanitization. Using DOMPurify's official addHook API avoids any manual
// innerHTML reads/writes (which trip static analyzers as XSS sinks even when
// operating on already-sanitized output).
let __dompurifyHookInstalled = false;
function installLinkHardeningHook() {
  if (__dompurifyHookInstalled || typeof window === 'undefined') return;
  DOMPurify.addHook('afterSanitizeAttributes', (node) => {
    if (node.tagName !== 'A') return;
    const href = node.getAttribute('href') || '';
    const isExternal =
      /^https?:\/\//i.test(href) && !href.includes(window.location.host);
    if (isExternal) {
      node.setAttribute('target', '_blank');
      node.setAttribute('rel', 'noopener noreferrer nofollow');
    }
  });
  __dompurifyHookInstalled = true;
}

function sanitizeHtml(raw) {
  if (!raw) return '';
  installLinkHardeningHook();
  return DOMPurify.sanitize(raw, SANITIZE_CONFIG);
}

function formatDate(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  } catch {
    return '';
  }
}

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [status, setStatus] = useState('loading');

  // Fetch the post when the slug changes. The setState calls inside the
  // promise chain are intentional (loading → success / error transitions),
  // so the set-state-in-effect advisory is disabled for the effect body.
  
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'instant' });
    }
    let active = true;
    setStatus('loading');
    api.get(`/api/blog/posts/${encodeURIComponent(slug)}`)
      .then((data) => { if (active) { setPost(data); setStatus('ok'); } })
      .catch((err) => {
        logger.error('Failed to load blog post', err);
        if (active) setStatus(err?.status === 404 ? 'notfound' : 'error');
      });
    return () => { active = false; };
  }, [slug]);

  const canonicalUrl = post?.canonical_url || `${SITE_BASE}/blog/${slug}`;
  const ogImage = post?.hero_image_url
    ? (post.hero_image_url.startsWith('http') ? post.hero_image_url : `${SITE_BASE}${post.hero_image_url}`)
    : `${SITE_BASE}${DEFAULT_HERO}`;
  const metaTitle = post?.meta_title || (post ? `${post.title} | Apex Athletic Mind` : 'Loading…');
  const metaDescription = post?.meta_description || post?.excerpt || post?.subtitle || '';

  useDocumentHead({
    title: metaTitle,
    description: metaDescription,
    canonical: canonicalUrl,
    ogTitle: post?.title,
    ogDescription: metaDescription,
    ogImage,
    ogType: 'article',
    keywords: post?.tags?.join(', '),
    jsonLd: post && {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: post.title,
      description: metaDescription,
      image: [ogImage],
      datePublished: post.published_at,
      dateModified: post.updated_at || post.published_at,
      author: {
        '@type': 'Person',
        name: AUTHOR_NAME,
        url: SITE_BASE,
      },
      publisher: {
        '@type': 'Organization',
        name: 'Apex Athletic Mind Consulting LLC',
        url: SITE_BASE,
      },
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': canonicalUrl,
      },
      keywords: (post.tags || []).join(', '),
    },
  });

  // NOTE: must be declared BEFORE any conditional return so hook order
  // is stable across renders (Rules of Hooks).
  const sanitizedHtml = useMemo(() => sanitizeHtml(post?.html), [post?.html]);

  if (status === 'notfound') {
    return <Navigate to="/blog" replace />;
  }

  return (
    <BlogShell testId="blog-post-page">
      <article className="max-w-[1120px] mx-auto px-5 sm:px-8 pt-8 sm:pt-10 pb-20">
        {/* Breadcrumbs */}
        <nav
          aria-label="Breadcrumb"
          className="mb-6 text-[12.5px] text-[#6b6b6b] flex items-center gap-1.5 flex-wrap"
          data-testid="blog-breadcrumbs"
        >
          <Link to="/" className="hover:text-[#004C54] transition-colors">Home</Link>
          <ChevronRight className="h-3.5 w-3.5" />
          <Link to="/blog" className="hover:text-[#004C54] transition-colors">The Apex Journal</Link>
          <ChevronRight className="h-3.5 w-3.5" />
          <span className="text-[#1a1a1a] truncate max-w-[40ch]">{post?.title || '...'}</span>
        </nav>

        {status === 'loading' && (
          <div className="space-y-5" data-testid="blog-post-loading">
            <Skeleton className="h-5 w-40" />
            <Skeleton className="h-12 w-3/4" />
            <Skeleton className="h-6 w-1/2" />
            <Skeleton className="aspect-[16/9] w-full rounded-2xl" />
            <div className="max-w-[68ch] mx-auto space-y-3 pt-6">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-[95%]" />
              <Skeleton className="h-4 w-[90%]" />
              <Skeleton className="h-4 w-[88%]" />
            </div>
          </div>
        )}

        {status === 'error' && (
          <div className="rounded-2xl border border-red-200 bg-red-50 p-6 text-[14px] text-red-700" data-testid="blog-post-error">
            We couldn&rsquo;t load this essay. Please refresh, or head back to <Link to="/blog" className="underline">The Apex Journal</Link>.
          </div>
        )}

        {post && (
          <>
            {/* Header */}
            <header className="max-w-[68ch] mx-auto mb-10 sm:mb-12">
              {post.tags?.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-4" data-testid="blog-post-tags">
                  {post.tags.map((t) => (
                    <span
                      key={t}
                      className="font-mono-accent uppercase tracking-[0.2em] text-[10px] text-[#004C54] font-semibold"
                    >#{t}</span>
                  ))}
                </div>
              )}
              <h1
                className="font-heading text-[2.1rem] sm:text-[2.85rem] font-bold tracking-tight leading-[1.08] text-[#1a1a1a] mb-5"
                data-testid="blog-post-title"
              >
                {post.title}
              </h1>
              {post.subtitle && (
                <p className="text-[17px] sm:text-[19px] text-[#3a3a3a] leading-[1.55] max-w-[60ch] mb-6">
                  {post.subtitle}
                </p>
              )}
              <div className="flex items-center gap-4 text-[13px] text-[#6b6b6b]" data-testid="blog-post-byline">
                <span className="font-semibold text-[#1a1a1a]">{AUTHOR_NAME}</span>
                <span className="text-[#c0c0c0]">·</span>
                <span>{formatDate(post.published_at)}</span>
                {post.reading_time_min ? (
                  <>
                    <span className="text-[#c0c0c0]">·</span>
                    <span className="inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5" />{post.reading_time_min} min read</span>
                  </>
                ) : null}
              </div>
            </header>

            {/* Hero image */}
            {(post.hero_image_url || DEFAULT_HERO) && (
              <figure
                className="relative aspect-[16/9] w-full overflow-hidden rounded-2xl sm:rounded-3xl shadow-xl shadow-black/10 mb-10 sm:mb-14 bg-[#0a0d0e]"
                data-testid="blog-post-hero"
              >
                <img
                  src={post.hero_image_url || DEFAULT_HERO}
                  alt={post.title}
                  className="absolute inset-0 h-full w-full object-cover"
                  loading="eager"
                />
              </figure>
            )}

            {/* Article body — sanitized via DOMPurify allowlist (see SANITIZE_CONFIG) */}
            <div
              className="prose-blog max-w-[68ch] mx-auto text-[17px] leading-[1.78] text-[#2a2a2a]"
              data-testid="blog-post-body"
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
            />

            {/* Inline CTA block */}
            <section
              className="max-w-[68ch] mx-auto mt-12 sm:mt-16 rounded-3xl bg-[#0B0C0D] text-white p-8 sm:p-10 shadow-xl"
              data-testid="blog-post-cta"
            >
              <div className="font-mono-accent uppercase tracking-[0.22em] text-[10px] text-[#4FBCC7] font-semibold mb-2">
                Train With Coach Lewis
              </div>
              <h2 className="font-heading text-[1.5rem] sm:text-[1.75rem] font-bold tracking-tight mb-3">
                Stop guessing. Start competing.
              </h2>
              <p className="text-[15px] text-white/75 leading-[1.65] max-w-[50ch] mb-6">
                The Apex Six Athlete Self-Assessment is an 8-minute deep-dive that returns a personalized Individual Development Plan built around your pressure profile.
              </p>
              <div className="flex flex-wrap gap-3">
                <Button asChild className="bg-white text-[#0B0C0D] hover:bg-white/90 h-11 px-5 rounded-xl font-semibold" data-testid="blog-cta-assessment">
                  <Link to="/assessment">Take the $27 Self-Assessment</Link>
                </Button>
                <Button asChild variant="outline" className="h-11 px-5 rounded-xl border-white/30 bg-transparent text-white hover:bg-white/10 hover:text-white" data-testid="blog-cta-quiz">
                  <Link to="/quiz">Try the Free Mindset Quiz</Link>
                </Button>
              </div>
            </section>

            {/* Related posts */}
            {post.related?.length > 0 && (
              <section className="mt-16 sm:mt-20" data-testid="blog-post-related">
                <div className="font-mono-accent uppercase tracking-[0.22em] text-[10px] text-[#004C54] font-semibold mb-4">
                  Keep Reading
                </div>
                <Separator className="mb-8 bg-black/8" />
                <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                  {post.related.map((r) => (
                    <Link
                      key={r.id}
                      to={`/blog/${r.slug}`}
                      className="group block rounded-2xl overflow-hidden border border-black/8 bg-white shadow-sm hover:shadow-lg transition-shadow"
                      data-testid={`blog-related-${r.slug}`}
                    >
                      <div className="aspect-[16/10] overflow-hidden bg-[#0a0d0e]">
                        <img
                          src={r.hero_image_url || DEFAULT_HERO}
                          alt={r.title}
                          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
                          loading="lazy"
                        />
                      </div>
                      <div className="p-5">
                        <h3 className="font-heading text-[1.05rem] font-semibold leading-snug text-[#1a1a1a] mb-2 group-hover:text-[#004C54] transition-colors">
                          {r.title}
                        </h3>
                        <span className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-[#004C54]">
                          Read <ArrowRight className="h-3.5 w-3.5" />
                        </span>
                      </div>
                    </Link>
                  ))}
                </div>
              </section>
            )}

            <div className="mt-14 max-w-[68ch] mx-auto">
              <Link
                to="/blog"
                className="inline-flex items-center gap-2 text-[13.5px] font-semibold text-[#004C54] hover:text-[#003a40] transition-colors"
                data-testid="blog-post-back"
              >
                <ArrowLeft className="h-4 w-4" />
                Back to The Apex Journal
              </Link>
            </div>
          </>
        )}
      </article>
    </BlogShell>
  );
}
